import { NumberFormat, Space } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import type { InvoiceTotals } from '@features/invoices/types/invoices/invoiceTotals'

import type { LocaleIdentifier } from '../../../../../../../../../../../../types/localeIdentifier'
import { useInvoiceEditorDocumentType } from '../../../../../../../../hooks/useInvoiceEditorDocumentType'
import { InvoiceSummaryLine } from '../InvoiceSummaryLine'
import { InvoiceSummaryTaxes } from '../InvoiceSummaryTaxes'
import { InvoiceSummaryTotal } from '../InvoiceSummaryTotal'
import * as Styled from './styles'

interface AmountDueSummaryProps {
  amountDue: InvoiceTotals
  currency: string
  locale: LocaleIdentifier
  shouldShowTaxBreakdown: boolean
}

export const AmountDueSummary = ({
  amountDue,
  currency,
  locale,
  shouldShowTaxBreakdown,
}: AmountDueSummaryProps): ReactElement => {
  const { t } = useTranslation()
  const { isCreditNote } = useInvoiceEditorDocumentType()

  const totalLabel = isCreditNote
    ? t('external_invoices.editor.deposit_summary.total_to_credit')
    : t('external_invoices.editor.deposit_summary.total_amount_due')

  const getDisplayedCurrency = (value: number) => NumberFormat.getCurrencyNumber(value, currency, locale)

  if (!shouldShowTaxBreakdown) {
    return (
      <>
        <Space size="s8" />
        <InvoiceSummaryTotal label={totalLabel} text={getDisplayedCurrency(amountDue.grossTotal)} />
      </>
    )
  }

  return (
    <Styled.AmountDueBreakdown>
      <InvoiceSummaryLine
        label={t('external_invoices.editor.summary.total_excluding_vat')}
        text={getDisplayedCurrency(amountDue.netTotal)}
      />
      <Space size="s8" />
      <InvoiceSummaryTaxes currency={currency} locale={locale} taxSummary={amountDue.taxSummary} />
      {amountDue.taxSummary.length > 1 && (
        <>
          <InvoiceSummaryLine
            label={t('external_invoices.editor.summary.total_vat')}
            text={getDisplayedCurrency(amountDue.taxTotal)}
          />
          <Space size="s8" />
        </>
      )}
      <InvoiceSummaryTotal label={totalLabel} text={getDisplayedCurrency(amountDue.grossTotal)} />
    </Styled.AmountDueBreakdown>
  )
}
